// @flow
import React, { Fragment, useContext, useState } from 'react';
import classnames from 'classnames';

import { ICONS } from 'Components/utils/icon/icons';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { t } from 'Src/i18n';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon';
import Image from 'Components/frameworks/image/Image';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { Slider } from 'Components/frameworks/slider/Slider';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Text } from 'Components/frameworks/text/Text';
import { TrackClick } from 'Tracking/track-click/TrackClick';

type BrandPromiseLineItem = {
  iconName: IconName,
  mediaUrl: string,
  mediaAlt?: string,
  link?: string,
  text: string,
  isExclusiveCollection?: boolean,
};

type Props = {
  id?: string,
  learnMoreLink?: string,
  learnMoreLabel?: string,
  title?: string,
  items: Array<BrandPromiseLineItem>,
};

function getBadgeStylesFromIcon(isExclusiveCollectionIcon) {
  if (isExclusiveCollectionIcon) {
    return {
      iconContainer: '',
      iconContainerWidth: '122',
      iconContainerHeight: '72',
      icon: 'border-5 border-current p-2',
      iconWidth: 'calc(100rem / 16)',
      isNotSquare: true,
    };
  }
  return {
    iconContainer: 'rounded-full',
    iconContainerWidth: '84',
    iconContainerHeight: '84',
    icon: '',
    iconWidth: 'calc(31rem / 16)',
    isNotSquare: false,
  };
}

function getTextStyles(isExclusiveCollection) {
  if (isExclusiveCollection) {
    return 'text-deepBlue font-serif';
  }
  return 'text-blue';
}

function getItemsToRender(items, hoveredItem, setHoveredItem) {
  const shouldShowPictures = items.every(item => !!item.mediaUrl);

  return items.map(({ text, iconName, isExclusiveCollection, mediaUrl, mediaAlt, link }, index) => {
    const TagName = link ? TrackClick : Fragment;
    const iconTitle = iconName || '';
    const trackingData = {
      actionGA: 'push_content_brandPromise',
      labelGA: `${iconTitle}`.trim(),
    };
    const conditionalTagNameProps = link ? { as: DynamicLink, url: link, trackingData } : {};

    const isXCIcon = iconName === ICONS.EXCLUSIVE_COLLECTION;
    const isXC = isExclusiveCollection || isXCIcon;
    const textStyles = getTextStyles(isXC);
    const badgeStyles = getBadgeStylesFromIcon(isXCIcon);
    /* I know how tedious this looks but we're concentrating the complexity
   in those four lines, the rest should be smooth sailing. I hope. */

    return (
      <div
        key={text}
        onMouseEnter={() => {
          setHoveredItem(index);
        }}
        onMouseLeave={() => {
          setHoveredItem(null);
        }}
        data-testid="BrandPromiseLine-item"
        className={classnames('relative rtl:ml-5', { 'lg:rtl:ml-0': index === 0 })}
      >
        {shouldShowPictures ? (
          <div
            style={{
              height: '235px',
              width: '180px',
              flexShrink: '0',
            }}
            className={classnames(
              'rounded-tiny p-3 overflow-hidden text-center flex items-end justify-center relative',
              { 'inset-border-after': isXC },
            )}
          >
            <Image
              className={classnames('absolute inset-0 transition-transform', {
                'scale-105': hoveredItem === index && link,
              })}
              altMsg={mediaAlt}
              imageUrl={mediaUrl}
              mandatoryWidthAndHeight={{ h: 235, w: 180 }}
            />
          </div>
        ) : null}
        <TagName {...conditionalTagNameProps}>
          {shouldShowPictures ? (
            <div className="relative" style={{ height: '235px', marginTop: '-235px' }} />
          ) : null}
          <div
            style={{
              width: '180px',
            }}
            className={textStyles}
          >
            <div
              style={{
                width: '126px',
                height: '72px',
                marginTop: shouldShowPictures ? '-42px' : '',
              }}
              className="relative m-auto overflow-hidden"
            >
              <div
                className={classnames(
                  'bg-white m-auto flex items-center justify-center',
                  badgeStyles.iconContainer,
                )}
                style={{
                  width: `calc(${badgeStyles.iconContainerWidth}rem / 16)`,
                  height: `calc(${badgeStyles.iconContainerHeight}rem / 16)`,
                }}
              >
                <Icon
                  name={iconName}
                  className={badgeStyles.icon}
                  width={badgeStyles.iconWidth}
                  isNotSquare={badgeStyles.isNotSquare}
                />
              </div>
            </div>
            <Text className="text-base text-center" color="current" text={text} />
          </div>
        </TagName>
      </div>
    );
  });
}

export const BrandPromiseLine = ({ id, title, items, learnMoreLink, learnMoreLabel }: Props) => {
  // use isDesktop to display slider's controls only on desktops
  const { isTablet, isDesktop } = useContext(ResponsiveContext);
  const [hoveredItem, setHoveredItem] = useState(null);

  return (
    <SiteContainer className="Line" id={id}>
      <div className="flex flex-wrap justify-between">
        {!!title && <LineTitle className="flex-1 md:flex-none">{title}</LineTitle>}
        {learnMoreLabel &&
          learnMoreLink && (
            <TrackClick
              as={DynamicLink}
              className="m-auto md:mx-0 mt-4 md:mt-1 order-last md:order-none text-base text-gray-darker hover:text-gray-active focus:text-gray-active underline"
              trackingData={{
                actionGA: 'push_content_brandPromise',
                labelGA: 'learnMore',
              }}
              url={learnMoreLink}
            >
              {learnMoreLabel}
            </TrackClick>
          )}
        <div className="-mx-5 lg:mx-0 w-screen lg:w-full">
          <Slider
            cols={0}
            hasControls={isDesktop}
            nbItemsToScroll={isTablet ? 2 : 1}
            scrollAreaClassName="overflow-hidden"
            scrollAreaContainerClassName="overflow-auto scroll-snap-area scroll-snap-type-x-mandatory"
            scrollBackwardLabel={t('common.scrollBackward')}
            scrollForwardLabel={t('common.scrollForward')}
            scrollItemClassName="overflow-hidden flex-none max-w-full"
            navPosition="center"
            scrollControlsClassName="-mx-5 lg:mx-0"
          >
            {getItemsToRender(items, hoveredItem, setHoveredItem)}
            <div
              style={{ width: '20px', height: '1px', flexShrink: '0' }}
              className="lg:hidden rtl:-order-1"
            />
          </Slider>
        </div>
      </div>
    </SiteContainer>
  );
};
